import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Home,
  Calendar,
  DollarSign,
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Building,
  Hammer,
  Droplets,
  Shield,
  Clock,
  Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_jax-craft-remodel/artifacts/6r3h6sd1_Transparent%20Logo.png";

export const ClientIntakeForm = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    // Client Information
    client_name: '',
    email: '',
    phone: '',
    preferred_contact: 'email',
    property_address: '',
    property_occupied: 'owner_occupied',
    property_type: 'single_family',
    referral_source: '',
    
    // Project Discovery
    project_types: [],
    project_description: '',
    problem_to_solve: '',
    desired_start: 'within_30_days',
    budget_range: 'prefer_not_say',
    deadlines: '',
    solution_preference: 'best_long_term',
    
    // Project Conditions
    existing_reports: [],
    historic_district: 'no',
    water_damage: 'no',
    wdo_damage: 'no',
    structural_concerns: 'no',
    permits_required: 'unsure',
    vegetation_removal: 'owner',
    lift_access_required: 'no',
    
    uploaded_files: []
  });

  useEffect(() => {
    fetchLinkData();
  }, [token]);

  const fetchLinkData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/proposals/intake-link/${token}`);
      setLinkData(response.data);
      setFormData(prev => ({
        ...prev,
        client_name: response.data.client_name || '',
        email: response.data.client_email || ''
      }));
      
      if (response.data.status === 'completed') {
        setError('This intake form has already been submitted.');
      }
    } catch (err) {
      if (err.response?.status === 410) {
        setError('This intake link has expired. Please contact Jax Remodeling Solutions for a new link.');
      } else if (err.response?.status === 404) {
        setError('Invalid intake link. Please contact Jax Remodeling Solutions.');
      } else {
        setError('Unable to load intake form. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/proposals/intake-link/${token}/submit`, formData);
      toast.success('Thank you! Your project information has been submitted.');
      setStep(4); // Show success
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleArrayValue = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const projectTypeOptions = [
    { value: 'exterior_painting', label: 'Exterior Painting' },
    { value: 'interior_painting', label: 'Interior Painting' },
    { value: 'historic_restoration', label: 'Historic Restoration' },
    { value: 'wdo_repairs', label: 'WDO Repairs' },
    { value: 'siding_repair', label: 'Siding Repair' },
    { value: 'drywall_repair', label: 'Drywall Repair' },
    { value: 'bathroom_remodel', label: 'Bathroom Remodel' },
    { value: 'kitchen_remodel', label: 'Kitchen Remodel' },
    { value: 'deck_repair', label: 'Deck Repair' },
    { value: 'fence', label: 'Fence' },
    { value: 'roofing', label: 'Roofing' },
    { value: 'general_repairs', label: 'General Repairs' },
    { value: 'other', label: 'Other' }
  ];

  const reportOptions = [
    { value: 'wdo_report', label: 'WDO Report' },
    { value: 'home_inspection', label: 'Home Inspection Report' },
    { value: 'insurance_scope', label: 'Insurance Scope' },
    { value: 'engineering', label: 'Engineering Report' },
    { value: 'architectural', label: 'Architectural Plans' },
    { value: 'historic_preservation', label: 'Historic Preservation Requirements' },
    { value: 'permit_comments', label: 'Permit Comments' },
    { value: 'hoa', label: 'HOA Requirements' },
    { value: 'none', label: 'None' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Form</h1>
          <p className="text-gray-600">{error}</p>
          <p className="mt-4 text-sm text-gray-500">
            Contact us at (904) 863-6673 or info@jaxremodelingsolutions.com
          </p>
        </div>
      </div>
    );
  }

  if (step === 4) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600">Your project information has been submitted successfully.</p>
          <p className="mt-4 text-gray-500">
            A member of our team will review your information and contact you within 1-2 business days.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1a1a1a] py-6">
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-center">
          <img src={LOGO_URL} alt="Jax Remodeling Solutions" className="h-12" />
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s === step ? 'bg-[#C6A87C] text-white' : 
                  s < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s < step ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                <span className={`ml-2 text-sm ${s === step ? 'text-[#C6A87C] font-medium' : 'text-gray-500'}`}>
                  {s === 1 ? 'Your Info' : s === 2 ? 'Project Details' : 'Conditions'}
                </span>
                {s < 3 && <div className={`w-16 h-0.5 mx-4 ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          
          {/* Step 1: Client Information */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Client Information</h2>
                <p className="text-gray-500 text-sm">Tell us about yourself and your property</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={formData.client_name}
                      onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Preferred Contact Method</label>
                  <div className="flex gap-4">
                    {['phone', 'text', 'email'].map(method => (
                      <label key={method} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="preferred_contact"
                          value={method}
                          checked={formData.preferred_contact === method}
                          onChange={(e) => setFormData({...formData, preferred_contact: e.target.value})}
                          className="text-[#C6A87C] focus:ring-[#C6A87C]"
                        />
                        <span className="capitalize text-sm">{method}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Property Address *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.property_address}
                    onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                    placeholder="Full street address, city, state, zip"
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Is the Property Occupied?</label>
                  <select
                    value={formData.property_occupied}
                    onChange={(e) => setFormData({...formData, property_occupied: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  >
                    <option value="owner_occupied">Owner Occupied</option>
                    <option value="tenant_occupied">Tenant Occupied</option>
                    <option value="vacant">Vacant</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Property Type</label>
                  <select
                    value={formData.property_type}
                    onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  >
                    <option value="single_family">Single Family</option>
                    <option value="duplex">Duplex</option>
                    <option value="multi_family">Multi-Family</option>
                    <option value="commercial">Commercial</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">How Did You Hear About Us?</label>
                <input
                  type="text"
                  value={formData.referral_source}
                  onChange={(e) => setFormData({...formData, referral_source: e.target.value})}
                  placeholder="Referral, Google, Yelp, etc."
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                />
              </div>
            </div>
          )}

          {/* Step 2: Project Discovery */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Project Discovery</h2>
                <p className="text-gray-500 text-sm">Tell us about your project</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">What Type of Project Is This? *</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {projectTypeOptions.map(opt => (
                    <label key={opt.value} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.project_types.includes(opt.value) 
                        ? 'border-[#C6A87C] bg-[#C6A87C]/10' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="checkbox"
                        checked={formData.project_types.includes(opt.value)}
                        onChange={() => toggleArrayValue('project_types', opt.value)}
                        className="text-[#C6A87C] focus:ring-[#C6A87C]"
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Please Describe Your Project *</label>
                <textarea
                  value={formData.project_description}
                  onChange={(e) => setFormData({...formData, project_description: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  placeholder="Please provide as much detail as possible..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">What Problem Are You Trying To Solve?</label>
                <textarea
                  value={formData.problem_to_solve}
                  onChange={(e) => setFormData({...formData, problem_to_solve: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">When Would You Like Work To Begin?</label>
                  <select
                    value={formData.desired_start}
                    onChange={(e) => setFormData({...formData, desired_start: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  >
                    <option value="immediately">Immediately</option>
                    <option value="within_30_days">Within 30 Days</option>
                    <option value="within_60_days">Within 60 Days</option>
                    <option value="within_90_days">Within 90 Days</option>
                    <option value="gathering_info">Just Gathering Information</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Do You Have A Budget In Mind?</label>
                  <select
                    value={formData.budget_range}
                    onChange={(e) => setFormData({...formData, budget_range: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  >
                    <option value="under_5k">Under $5,000</option>
                    <option value="5k_10k">$5,000 - $10,000</option>
                    <option value="10k_25k">$10,000 - $25,000</option>
                    <option value="25k_50k">$25,000 - $50,000</option>
                    <option value="over_50k">Over $50,000</option>
                    <option value="prefer_not_say">Prefer Not To Say</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Are There Any Deadlines We Should Know About?</label>
                <input
                  type="text"
                  value={formData.deadlines}
                  onChange={(e) => setFormData({...formData, deadlines: e.target.value})}
                  placeholder="e.g., Need complete before selling, insurance deadline, etc."
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">What Type of Solution Are You Looking For?</label>
                <div className="grid md:grid-cols-3 gap-3">
                  {[
                    { value: 'lowest_cost', label: 'Lowest Cost Option', desc: 'Budget-focused approach' },
                    { value: 'best_long_term', label: 'Best Long-Term Solution', desc: 'Quality & durability focused' },
                    { value: 'multiple_options', label: 'Multiple Options', desc: 'Compare different approaches' }
                  ].map(opt => (
                    <label key={opt.value} className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      formData.solution_preference === opt.value 
                        ? 'border-[#C6A87C] bg-[#C6A87C]/10' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="solution_preference"
                        value={opt.value}
                        checked={formData.solution_preference === opt.value}
                        onChange={(e) => setFormData({...formData, solution_preference: e.target.value})}
                        className="sr-only"
                      />
                      <p className="font-medium text-sm">{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-1">{opt.desc}</p>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Project Conditions */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Project Conditions</h2>
                <p className="text-gray-500 text-sm">Help us understand your property's condition</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Have You Received Any Of The Following?</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {reportOptions.map(opt => (
                    <label key={opt.value} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.existing_reports.includes(opt.value) 
                        ? 'border-[#C6A87C] bg-[#C6A87C]/10' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="checkbox"
                        checked={formData.existing_reports.includes(opt.value)}
                        onChange={() => toggleArrayValue('existing_reports', opt.value)}
                        className="text-[#C6A87C] focus:ring-[#C6A87C]"
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Is The Property In A Historic District?</label>
                  <select
                    value={formData.historic_district}
                    onChange={(e) => setFormData({...formData, historic_district: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unsure">Unsure</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Is There Known Water Damage?</label>
                  <select
                    value={formData.water_damage}
                    onChange={(e) => setFormData({...formData, water_damage: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unsure">Unsure</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Is There Known Termite/WDO Damage?</label>
                  <select
                    value={formData.wdo_damage}
                    onChange={(e) => setFormData({...formData, wdo_damage: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unsure">Unsure</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Are There Structural Concerns?</label>
                  <select
                    value={formData.structural_concerns}
                    onChange={(e) => setFormData({...formData, structural_concerns: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unsure">Unsure</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Will The Property Require Permits?</label>
                  <select
                    value={formData.permits_required}
                    onChange={(e) => setFormData({...formData, permits_required: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unsure">Unsure</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Will Lift Access Be Required?</label>
                  <select
                    value={formData.lift_access_required}
                    onChange={(e) => setFormData({...formData, lift_access_required: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unsure">Unsure</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Who Is Responsible For Vegetation Removal?</label>
                <select
                  value={formData.vegetation_removal}
                  onChange={(e) => setFormData({...formData, vegetation_removal: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C6A87C]"
                >
                  <option value="owner">Owner</option>
                  <option value="contractor">Contractor</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium mb-2">Upload Photos, Reports, Plans, or Documents</label>
                <p className="text-xs text-gray-500 mb-3">
                  Uploading existing reports, photos, or plans helps us provide a more accurate proposal.
                </p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">Drag & drop files or click to browse</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG up to 10MB each</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            {step > 1 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="px-6 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
              >
                Back
              </button>
            ) : <div />}
            
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="px-6 py-2 bg-[#C6A87C] text-white rounded-lg hover:bg-[#B09265] flex items-center gap-2"
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-6 py-2 bg-[#C6A87C] text-white rounded-lg hover:bg-[#B09265] flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Submit
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Jax Remodeling Solutions LLC</p>
          <p>Jacksonville, FL | (904) 863-6673 | info@jaxremodelingsolutions.com</p>
        </div>
      </div>
    </div>
  );
};

export default ClientIntakeForm;
